<template>
	<component
		:is="displayResponsive ? 'v-card' : 'v-navigation-drawer'"
		v-model="isExpanded"
		:width="displayResponsive ? '100%' : navigation.width"
		:height="displayResponsive ? 'calc(100svh - 55px)' : '100vh;'"
		location="left"
		permanent
		style="background-color: rgb(var(--v-theme-contained))"
	>
		<TopBar :session-id="session.sessionId" />
		<v-spacer />
		<div v-if="!displayResponsive" id="alternisborder"></div>
		<div :style="{ position: 'absolute', right: '-24px', top: 'calc(50% - 24px)' }">
			<v-btn
				:icon="isExpanded ? 'mdi-chevron-left' : 'mdi-chevron-right'"
				style="border-radius: 0 10px 10px 0; width: 24px; height: 60px"
				@click="toggleExpand()"
			></v-btn>
		</div>
		<div
			style="display: flex; flex-direction: column; height: calc(100vh - 50px)"
			:style="displayResponsive ? 'padding-bottom: 150px' : ''"
		>
			<v-container v-scroll.self="onScroll" ref="alternisScrollContainer" style="overflow-y: auto; flex: 1">
				<div v-for="message in sortedMessages">
					<Message
						v-if="message.words.length > 0 || message.isProcessing"
						:key="message.messageId"
						:message="message"
					/>
				</div>
				<v-btn
					v-if="alternisStore.isExpanded && showFollowHint"
					min-width="260"
					:model-value="showFollowHint"
					style="position: sticky; bottom: 0; margin-left: 70px; cursor: pointer; z-index: 9999"
					rounded="pill"
					@click="() => (following = true)"
				>
					<v-icon>mdi-chevron-down</v-icon>
					{{ symbolsStore.GUILabel("2283e094-8000-4fd0-a21d-c42c36cb3fc5") }}
				</v-btn>
			</v-container>
			<BottomBar id="bottomBar" :session-id="session.sessionId" />
		</div>
	</component>
	<AttributeModal
		:show="alternisStore.clickedHighlightAttributes.length > 0"
		@done="alternisStore.clickedHighlightAttributes = []"
	/>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch, nextTick } from "vue";
import { useDomainStore } from "../../../../store/domain";
import type { Session, Message as SessionMessage } from "@lr-notitia/notitia-alternis/schemas";
import { api } from "@lr-notitia/notitia-alternis/client";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";
import { useAlternisStore } from "../../../../store/alternis/index";
import { useSessionStore } from "../../../../store/alternis/session";
import { useAudioStore } from "../../../../store/alternis/audio";
import { useGraphStore } from "../../../../store/graph";
import { useSymbolsStore } from "../../../../store/symbols";
import router from "../../../../router";

import TopBar from "./TopBar.vue";
import BottomBar from "./BottomBar.vue";
import Message from "./Message.vue";
import AttributeModal from "./AttributeModal.vue";

let betaPresetId = router.currentRoute.value.params.presetId as string;

const alternisStore = useAlternisStore();
const domainStore = useDomainStore();
const sessionStore = useSessionStore();
const audioStore = useAudioStore();
const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();

const emits = defineEmits(["change", "createPerson", "createAnnotation"]);

let workspaceId = router.currentRoute.value.params.workspaceId as string;

const { displayResponsive } = useResponsiveLayout();

const alternisScrollContainer = ref<any>(null);
const isExpanded = computed(() => alternisStore.isExpanded);
const navigation = ref({ minWidth: 300, width: 300 });

const following = ref(true);
const userFiler = ref("");
const showFollowHint = computed(() => !following.value);
const session = computed<Session>(
	() =>
		sessionStore.session || {
			sessionId: "",
			users: [],
			messages: [],
			bots: [],
		},
);

const notificationMessages = computed(() => {
	return graphStore.descriptor.notifications
		.filter((n) => n.display === "modal")
		.map((n) => {
			let msg: SessionMessage = {
				messageId: n.id,
				userId: "",
				type: "system",
				words: [{ value: n.description, probability: 1 }],
				lang: domainStore.languagePreference,
				translation: {},
				timestamp: n.timestamp,
				isProcessing: false,
			};
			return msg;
		});
});
const sortedMessages = computed(() => {
	return session.value.messages.concat(notificationMessages.value).sort((a, b) => a.timestamp - b.timestamp);
});

function onScroll(e: any) {
	const tolerance = 50;
	if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - tolerance) {
		following.value = true;
	} else {
		following.value = false;
	}
}

function scrollToBottom() {
	if (!alternisScrollContainer.value) return;
	const el = alternisScrollContainer.value.$el;
	el.scrollTop = el.scrollHeight;
}

watch(
	() => sortedMessages.value.length,
	() => {
		if (following.value) {
			nextTick(scrollToBottom);
		}
	},
);

function setEvents() {
	const el = document.getElementById("alternisborder");
	let isDragged = false;
	if (!el) return;

	function setWidth(width: number) {
		let maxWidth = window.innerWidth * 0.3;
		if (width < navigation.value.minWidth) {
			width = navigation.value.minWidth;
			localStorage.setItem("widthAlternis", String(navigation.value.minWidth));
		} else if (width > maxWidth) {
			width = maxWidth;
			localStorage.setItem("widthAlternis", String(maxWidth));
		} else {
			navigation.value.width = width;
			localStorage.setItem("widthAlternis", String(navigation.value.width));
		}
	}
	function resize(e: any) {
		e.preventDefault();
		e.stopPropagation();
		if (!el) return;
		document.body.style.cursor = "ew-resize";
		let f = e.clientX - 71;
		setWidth(f);
	}

	el.addEventListener(
		"pointerdown",
		function (e: any) {
			isDragged = true;
			if (e.offsetX < 2) {
				el.style.transition = "initial";
				document.addEventListener("pointermove", resize, false);
			}
		},
		false,
	);
	document.addEventListener(
		"pointerup",
		function (e: any) {
			if (!isDragged) return;
			el.style.transition = "";
			let f = e.clientX - 71;
			setWidth(f);
			document.body.style.cursor = "";
			document.removeEventListener("pointermove", resize, false);
			isDragged = false;
		},
		false,
	);
}

function getExpand(): boolean {
	const expandAlternis = localStorage.getItem("expandAlternis") || "true";
	return expandAlternis === "true";
}
function toggleExpand() {
	localStorage.setItem("expandAlternis", String(!isExpanded.value));
	alternisStore.isExpanded = !alternisStore.isExpanded;
	if (alternisStore.isExpanded) navigation.value.width = Math.max(getWidth(), navigation.value.minWidth);
	else navigation.value.width = navigation.value.minWidth;
}
function getWidth(): number {
	const widthAlternis = localStorage.getItem("widthAlternis") || navigation.value.minWidth;
	return Number(widthAlternis);
}

onMounted(async () => {
	alternisStore.isExpanded = getExpand();
	if (isExpanded.value) navigation.value.width = getWidth();
	setEvents();

	let session = await sessionStore.loadSession(workspaceId).catch((err) => console.error(err));
	if (!session) await sessionStore.createSession(workspaceId);
	if (betaPresetId && !sessionStore.session?.bots.includes(betaPresetId)) {
		await api().session().bots().post(workspaceId, betaPresetId);
	}
	sessionStore.loadSession(workspaceId).catch((err) => console.error(err));
});

onUnmounted(() => {
	sessionStore.clear();
	audioStore.leave();
});
</script>

<style lang="scss">
#alternisborder {
	position: absolute;
	right: 0;
	top: 0;
	width: 2px;
	height: 100%;
	background-color: grey;
	z-index: 999;
	cursor: ew-resize;
	user-select: none;
}
</style>
